import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, PencilSimpleLine, Signature } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DateField } from "@/components/DateField";
import { api, formatApiErrorDetail } from "@/lib/api";

const METHODS = ["Check", "Cash", "Card", "ACH", "Zelle", "Other"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
      {label}
    </span>
    {children}
  </label>
);

// Raise a deposit / progress / final invoice on the project for any amount.
export const IssueInvoiceDialog = ({ open, onClose, project, onDone }) => {
  const fin = project.finance || {};
  const remaining = Math.max(0, Number(fin.ordered || 0) - Number(fin.invoiced || 0));
  const [form, setForm] = useState({
    kind: "progress",
    amount: "",
    title: "",
    due_date: "",
    notes: "",
    po_number: "",
    send: true,
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm({
      kind: remaining > 0 ? "final" : "custom",
      amount: remaining > 0 ? remaining.toFixed(2) : "",
      title: "",
      due_date: "",
      notes: "",
      po_number: "",
      send: !!project.customer_email || true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const preset = (kind) => {
    const map = {
      progress: form.amount,
      final: remaining > 0 ? remaining.toFixed(2) : "",
      custom: form.amount,
    };
    set({ kind, amount: map[kind] });
  };
  const submit = async () => {
    const amount = Number(form.amount);
    if (!(amount > 0)) {
      toast.error("Enter the invoice amount");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/invoices`, { ...form, amount });
      toast.success(`${data.invoice?.code || "Invoice"} issued for $${amount.toFixed(2)}`, {
        description: data.emailed_to
          ? `Emailed to ${data.emailed_to}`
          : form.send
            ? "No customer email on file — not emailed"
            : "Saved as sent, not emailed",
      });
      onDone?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Could not issue the invoice"));
    }
    setBusy(false);
  };
  const KINDS = [
    ["progress", "Progress"],
    ["final", "Final balance"],
    ["custom", "Custom"],
  ];
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md border-zinc-800 bg-zinc-950" data-testid="issue-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Issue invoice</DialogTitle>
        </DialogHeader>
        <p className="-mt-1 text-xs text-zinc-500">
          Ordered ${Number(fin.ordered || 0).toFixed(2)} · invoiced ${Number(fin.invoiced || 0).toFixed(2)} ·{" "}
          <span className="font-semibold text-zinc-300">${remaining.toFixed(2)} left to invoice</span>
        </p>
        <div className="grid grid-cols-3 gap-1 rounded-lg bg-zinc-900 p-1" data-testid="issue-invoice-kind">
          {KINDS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => preset(k)}
              data-testid={`issue-invoice-kind-${k}`}
              className={`h-8 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors duration-150 ${
                form.kind === k ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Amount ($)">
            <Input
              data-testid="issue-invoice-amount"
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={(e) => set({ amount: e.target.value })}
              className="h-10 border-zinc-800 bg-zinc-900"
              autoFocus
            />
          </Field>
          <Field label="Due date">
            <DateField
              testid="issue-invoice-due"
              value={form.due_date}
              onChange={(v) => set({ due_date: v })}
              placeholder="Pick a due date"
            />
          </Field>
        </div>
        <Field label="Title (optional)">
          <Input
            data-testid="issue-invoice-title"
            value={form.title}
            onChange={(e) => set({ title: e.target.value })}
            placeholder={`${KINDS.find(([k]) => k === form.kind)?.[1]} — ${project.name || "Project"}`}
            className="h-10 border-zinc-800 bg-zinc-900"
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="PO number">
            <Input
              data-testid="issue-invoice-po"
              value={form.po_number}
              onChange={(e) => set({ po_number: e.target.value })}
              className="h-10 border-zinc-800 bg-zinc-900"
            />
          </Field>
          <label className="flex items-end gap-2 pb-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              data-testid="issue-invoice-send"
              checked={form.send}
              onChange={(e) => set({ send: e.target.checked })}
              className="h-4 w-4 accent-indigo-500"
            />
            Email to customer now
          </label>
        </div>
        <Field label="Notes on invoice">
          <Textarea
            data-testid="issue-invoice-notes"
            rows={2}
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
            className="border-zinc-800 bg-zinc-900"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="issue-invoice-cancel">
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={busy} data-testid="issue-invoice-submit">
            {busy ? "Issuing…" : "Issue invoice"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Take money on a project at any stage — before the quote or drawing is accepted.
export const ReceivePaymentDialog = ({ open, onClose, project, invoices = [], onDone, initialInvoiceId = "" }) => {
  const openInvoices = invoices.filter(
    (i) => i.status !== "Void" && i.status !== "Paid" && i.balance > 0.009,
  );
  const [form, setForm] = useState({
    amount: "",
    method: "Check",
    reference: "",
    note: "",
    at: new Date().toISOString().slice(0, 10),
    invoice_id: "",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const first = openInvoices.find((i) => i.id === initialInvoiceId) || openInvoices[0];
    setForm({
      amount: first ? String(first.balance) : "",
      method: "Check",
      reference: "",
      note: "",
      at: new Date().toISOString().slice(0, 10),
      invoice_id: first ? first.id : "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const submit = async () => {
    const amount = Number(form.amount);
    if (!(amount > 0)) {
      toast.error("Enter the amount received");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/payments`, {
        ...form,
        amount,
        at: form.at ? new Date(`${form.at}T12:00:00`).toISOString() : "",
      });
      toast.success(
        form.invoice_id
          ? `Payment applied to ${data.invoice?.code || "the invoice"}`
          : `Payment recorded — deposit invoice ${data.invoice?.code || ""} raised`,
        data.receipt_to
          ? { description: `Receipt emailed to ${data.receipt_to}` }
          : { description: "No customer email on file — no receipt sent" },
      );
      onDone?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Could not record the payment"));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md border-zinc-800 bg-zinc-950"
        data-testid="receive-payment-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-base">Receive payment</DialogTitle>
        </DialogHeader>
        <p className="-mt-1 text-xs text-zinc-500">
          {openInvoices.length
            ? `Pick the invoice this money applies to. ${openInvoices.length} open invoice${openInvoices.length > 1 ? "s" : ""} on this project.`
            : "No open invoices yet — a deposit invoice will be raised for this amount and marked paid."}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Amount ($)">
            <Input
              data-testid="receive-payment-amount"
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={(e) => set({ amount: e.target.value })}
              className="h-10 border-zinc-800 bg-zinc-900"
              autoFocus
            />
          </Field>
          <Field label="Received on">
            <DateField
              testid="receive-payment-date"
              value={form.at}
              onChange={(v) => set({ at: v })}
              placeholder="Today"
            />
          </Field>
          <Field label="Method">
            <select
              data-testid="receive-payment-method"
              value={form.method}
              onChange={(e) => set({ method: e.target.value })}
              className="h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 text-sm text-zinc-100"
            >
              {METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reference / check #">
            <Input
              data-testid="receive-payment-reference"
              value={form.reference}
              onChange={(e) => set({ reference: e.target.value })}
              className="h-10 border-zinc-800 bg-zinc-900"
            />
          </Field>
        </div>
        <Field label={openInvoices.length ? "Apply to invoice" : "Apply to"}>
          <select
            data-testid="receive-payment-invoice"
            value={form.invoice_id}
            onChange={(e) => {
              const inv = openInvoices.find((i) => i.id === e.target.value);
              set({
                invoice_id: e.target.value,
                amount: inv ? String(inv.balance) : form.amount,
              });
            }}
            className={`h-10 w-full rounded-md border bg-zinc-900 px-3 text-sm text-zinc-100 ${
              openInvoices.length ? "border-indigo-500/50" : "border-zinc-800"
            }`}
          >
            <option value="">New deposit invoice for this amount</option>
            {openInvoices.map((i) => (
              <option key={i.id} value={i.id}>
                {`${i.code} · ${i.title} · $${Number(i.balance).toFixed(2)} due`}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Note">
          <Textarea
            data-testid="receive-payment-note"
            rows={2}
            value={form.note}
            onChange={(e) => set({ note: e.target.value })}
            className="border-zinc-800 bg-zinc-900"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            data-testid="receive-payment-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="receive-payment-submit"
            className="bg-emerald-600 text-white hover:bg-emerald-500"
          >
            {busy ? "Saving…" : "Record payment"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Staff sign-off on the customer's behalf: accept the quote, or approve the drawing.
export const AcceptDialog = ({ open, onClose, project, quote, mode, onDone }) => {
  const isDrawing = mode === "drawing";
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setName(project?.customer_name || "");
      setNote("");
    }
  }, [open, project]);
  const submit = async () => {
    setBusy(true);
    try {
      const url = `/projects/${project.id}/${isDrawing ? "approve-drawing" : "accept-quote"}`;
      const { data } = await api.post(url, { quote_id: quote.id, name, note });
      toast.success(
        isDrawing
          ? `Drawing on ${quote.code} approved`
          : data.already
            ? `${quote.code} was already accepted`
            : `${quote.code} accepted${data.job_code ? ` — job ${data.job_code} created` : ""}`,
      );
      onDone?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Could not save the sign-off"));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md border-zinc-800 bg-zinc-950"
        data-testid="accept-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-base">
            {isDrawing ? "Approve drawing" : "Accept quote"} · {quote?.code}
          </DialogTitle>
        </DialogHeader>
        <p className="-mt-1 text-xs text-zinc-500">
          {isDrawing
            ? "Records the customer's drawing sign-off taken in person or by phone, so fabrication can proceed."
            : "Marks the quote accepted for the customer, starts the project and raises the deposit and balance invoices — same as a portal acceptance."}
        </p>
        <Field label="Accepted by (customer name)">
          <Input
            data-testid="accept-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-10 border-zinc-800 bg-zinc-900"
          />
        </Field>
        <Field label="How / note">
          <Textarea
            data-testid="accept-note"
            rows={2}
            placeholder="e.g. Signed in the showroom, verbal OK by phone…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="border-zinc-800 bg-zinc-900"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="accept-cancel">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="accept-submit"
            className="bg-indigo-600 text-white hover:bg-indigo-500"
          >
            {busy ? "Saving…" : isDrawing ? "Approve drawing" : "Accept quote"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Pill = ({ ok, children, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
      ok ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-400"
    }`}
  >
    {ok && <CheckCircle size={11} weight="fill" />}
    {children}
  </span>
);

const SmallBtn = ({ testId, onClick, icon: Icon, children }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="flex h-7 items-center gap-1 rounded-md border border-indigo-500/50 bg-indigo-500/10 px-2 text-[10px] font-bold uppercase tracking-wider text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20"
  >
    {Icon && <Icon size={12} weight="bold" />}
    {children}
  </button>
);

// Quote + drawing sign-off state for one quote, with the office actions when still open.
export const QuoteSignoff = ({ quote, onAccept, onApprove, day, compact = false }) => {
  const accepted = quote.status === "Accepted";
  const approved = quote.drawing_status === "approved";
  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${compact ? "" : "text-xs"}`}
      data-testid={`quote-signoff-${quote.id}`}
    >
      <Pill ok={accepted} testId={`quote-accepted-pill-${quote.id}`}>
        {accepted
          ? `Accepted${quote.accepted_via === "office" ? " in office" : quote.accepted_via === "portal" ? " online" : ""}`
          : "Not accepted"}
      </Pill>
      {accepted && quote.accepted_at && day && (
        <span className="text-[10px] text-zinc-500">
          {quote.accepted_by ? `${quote.accepted_by} · ` : ""}
          {day(quote.accepted_at)}
        </span>
      )}
      {!accepted && (
        <SmallBtn
          testId={`quote-accept-${quote.id}`}
          onClick={() => onAccept(quote)}
          icon={Signature}
        >
          Accept quote
        </SmallBtn>
      )}
      {quote.has_drawing && approved && (
        <Pill ok testId={`quote-drawing-pill-${quote.id}`}>Drawing approved</Pill>
      )}
    </div>
  );
};
